'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { usePostHog } from 'posthog-js/react';
import { Suspense, useEffect, useRef } from 'react';

import posthog from 'posthog-js';
import { PostHogProvider as PHProvider } from 'posthog-js/react';
import { useAuth, useUser, useOrganization } from '@clerk/nextjs';
import { useConsentManager } from '@c15t/nextjs';
import { isOAuthTelemetryPath } from '@/lib/posthog';
import { needsOnboarding } from '@/lib/onboarding/gate-claims';
import { isVaultTelemetryPath, vaultTelemetryBlocked } from '@/lib/vault-telemetry';
import {
  CONCEPTUAL_CONSENT_CATEGORY,
  conceptualIdentify,
  conceptualTrack,
  loadConceptualPixel,
  revokeConceptualPixel,
} from '@/lib/conceptual';

type Gtag = (...args: unknown[]) => void;

function getGtag(): Gtag | undefined {
  if (typeof window === 'undefined') return undefined;
  const g = (window as unknown as { gtag?: Gtag }).gtag;
  return typeof g === 'function' ? g : undefined;
}

function GA4AnalyticsInner() {
  const consentManager = useConsentManager();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const granted = consentManager?.has('measurement');

  useEffect(() => {
    if (consentManager === undefined) return;
    const gtag = getGtag();
    if (!gtag) return;
    gtag('consent', 'update', {
      analytics_storage: granted ? 'granted' : 'denied',
    });
  }, [granted, consentManager]);

  useEffect(() => {
    if (!granted || isOAuthTelemetryPath(pathname) || vaultTelemetryBlocked()) return;
    const gtag = getGtag();
    if (!gtag) return;
    const query = searchParams.toString();
    const path = query ? `${pathname}?${query}` : pathname;
    gtag('event', 'page_view', {
      page_path: path,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [pathname, searchParams, granted]);

  return null;
}

export function GA4Analytics() {
  return (
    <Suspense fallback={null}>
      <GA4AnalyticsInner />
    </Suspense>
  );
}

function ConceptualPageViews() {
  const consentManager = useConsentManager();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const granted = consentManager?.has(CONCEPTUAL_CONSENT_CATEGORY);
  const trackedPath = useRef<string | null>(null);

  useEffect(() => {
    const pixelKey = process.env.NEXT_PUBLIC_CONCEPTUAL_PIXEL_KEY;
    if (!pixelKey) return;

    // The pixel can't be unloaded once injected, so a withdrawal stops it here
    // rather than leaving it collecting for the rest of the session. Matches
    // how GA4 and PostHog react to a revoke in this file.
    if (!granted) {
      revokeConceptualPixel();
      trackedPath.current = null;
      return;
    }

    loadConceptualPixel(pixelKey);
  }, [granted, pathname]);

  // The vendor's own page view is switched off so that a withdrawal is
  // enforceable, which makes every page view ours to send — the landing one
  // included. Tracking the last path reported rather than the last path visited
  // keeps a skipped OAuth route from swallowing the page that follows it.
  useEffect(() => {
    if (!granted || isOAuthTelemetryPath(pathname) || vaultTelemetryBlocked()) return;

    const query = searchParams.toString();
    const path = query ? `${pathname}?${query}` : pathname;
    if (trackedPath.current === path) return;

    trackedPath.current = path;
    conceptualTrack('page_view');
  }, [pathname, searchParams, granted]);

  return null;
}

function ConceptualIdentity() {
  const { isSignedIn } = useAuth();
  const { user } = useUser();
  const pathname = usePathname();
  const consentManager = useConsentManager();
  const granted = consentManager?.has(CONCEPTUAL_CONSENT_CATEGORY);
  const identifiedRef = useRef<string | null>(null);

  useEffect(() => {
    // A withdrawal deidentifies the pixel, so the identity has to be re-sent on
    // re-consent rather than suppressed as already done.
    if (!granted) {
      identifiedRef.current = null;
      return;
    }

    if (!isSignedIn || !user || isOAuthTelemetryPath(pathname) || vaultTelemetryBlocked()) return;

    // getKernelUserData is a round trip. Without this, a withdrawal or a
    // navigation to an OAuth route during it would still let the completion set
    // the ref — leaving the visitor marked as identified while the send itself
    // no-ops, so re-consent never re-identifies them.
    let cancelled = false;

    const identify = async () => {
      // Kernel user ID and email come from the database, never Clerk metadata.
      const { getKernelUserData } = await import('@/actions/user');
      const kernelData = await getKernelUserData();
      if (
        cancelled ||
        vaultTelemetryBlocked() ||
        !kernelData ||
        identifiedRef.current === kernelData.id
      )
        return;
      identifiedRef.current = kernelData.id;

      conceptualIdentify(kernelData.id, {
        email: kernelData.email,
        ...(user.fullName ? { name: user.fullName } : {}),
      });

      // Same definition of a new user the onboarding redirect uses.
      const isNewUser = needsOnboarding(user.publicMetadata);

      // signin is sent from the Clerk webhook instead, so CLI and MCP logins
      // are counted too. dedupe_id keeps a reload from counting a second signup.
      if (isNewUser) {
        conceptualTrack('signup', undefined, {
          dedupeId: `signup:${kernelData.id}`,
        });
      }
    };

    identify().catch((error) => {
      console.warn('Failed to identify user to Conceptual:', error);
    });

    return () => {
      cancelled = true;
    };
  }, [granted, isSignedIn, user, pathname]);

  return null;
}

export function ConceptualAnalytics() {
  return (
    <Suspense fallback={null}>
      <ConceptualPageViews />
      <ConceptualIdentity />
    </Suspense>
  );
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  return (
    <PHProvider client={posthog}>
      <PostHogConsentManager />
      <SuspendedPostHogPageView />
      <PostHogPageLeave />
      {children}
    </PHProvider>
  );
}

function PostHogPageView() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const posthogClient = usePostHog();

  // Track pageviews
  useEffect(() => {
    if (pathname && posthogClient && !vaultTelemetryBlocked()) {
      let url = window.origin + pathname;
      if (searchParams.toString()) {
        url = url + '?' + searchParams.toString();
      }

      posthogClient.capture('$pageview', { $current_url: url });
    }
  }, [pathname, posthogClient, searchParams]);

  return null;
}

function PostHogPageLeave() {
  // Send $pageleave so PostHog can compute bounce rate and session duration
  useEffect(() => {
    const captureLeave = () => {
      try {
        posthog.capture('$pageleave', { $current_url: window.location.href });
      } catch {}
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        captureLeave();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange, { passive: true });
    window.addEventListener('pagehide', captureLeave, { passive: true });
    window.addEventListener('beforeunload', captureLeave, { passive: true });

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange as EventListener);
      window.removeEventListener('pagehide', captureLeave as EventListener);
      window.removeEventListener('beforeunload', captureLeave as EventListener);
    };
  }, []);

  return null;
}

function PostHogConsentManager() {
  const pathname = usePathname();
  const { isSignedIn } = useAuth();
  const { user } = useUser();
  const { organization } = useOrganization();
  const consentManager = useConsentManager();

  // Check if user has given consent for measurement (analytics)
  const hasMeasurementConsent = consentManager?.has('measurement');

  // Effect 1: User identification (following Clerk's official pattern)
  // https://clerk.com/blog/how-to-use-clerk-with-posthog-identify-in-nextjs
  useEffect(() => {
    const identifyUser = async () => {
      // Identify if signed in and not already identified
      if (isSignedIn && user && !posthog._isIdentified()) {
        // Get Kernel user ID and email from DB (never use Clerk metadata)
        let kernelUserId: string | undefined;
        let userEmail: string | undefined;

        try {
          const { getKernelUserData } = await import('@/actions/user');
          const kernelData = await getKernelUserData();
          if (kernelData) {
            kernelUserId = kernelData.id;
            userEmail = kernelData.email;
          }
        } catch (error) {
          console.warn('Failed to fetch kernel user data:', error);
          return;
        }

        if (!kernelUserId) return;

        // Build user properties
        const userProperties: Record<string, string | Date> = {};

        if (user.fullName) {
          userProperties.name = user.fullName;
        } else if (user.firstName || user.lastName) {
          const firstName = user.firstName || '';
          const lastName = user.lastName || '';
          userProperties.name = `${firstName} ${lastName}`.trim();
        }
        if (user.createdAt) {
          userProperties.createdAt = user.createdAt;
        }
        if (userEmail) {
          userProperties.email = userEmail;
        }

        // Identify user
        posthog.identify(kernelUserId, userProperties);

        // Attach organization group (get from DB via server action)
        if (organization?.id) {
          try {
            const { getKernelOrgData } = await import('@/actions/org');
            const kernelOrgId = await getKernelOrgData(organization.id);
            if (kernelOrgId) {
              posthog.group?.('organization', kernelOrgId);
            }
          } catch (error) {
            console.warn('Failed to fetch kernel org data:', error);
          }
        }

        posthog.reloadFeatureFlags?.();
      }

      // Reset user data if they sign out (per Clerk's recommendation)
      if (!isSignedIn && posthog._isIdentified()) {
        posthog.reset();
      }
    };

    identifyUser();
  }, [isSignedIn, user, organization]);

  // Effect 2: Organization switching (handle when user switches orgs)
  const lastOrgIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isSignedIn || !organization?.id) return;

    // getKernelOrgData returns null when the client's organization.id is
    // ahead of the server-side auth() org id during a switch. Without a
    // retry, the effect won't re-run when the JWT later syncs, so the
    // PostHog group can stay on the previous org. Retry with backoff
    // until the server agrees or the org changes again.
    const targetOrgId = organization.id;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const attempt = async (delays: number[]) => {
      if (cancelled || targetOrgId === lastOrgIdRef.current) return;
      try {
        const { getKernelOrgData } = await import('@/actions/org');
        const kernelOrgId = await getKernelOrgData(targetOrgId);
        if (cancelled) return;
        if (kernelOrgId) {
          posthog.group?.('organization', kernelOrgId);
          lastOrgIdRef.current = targetOrgId;
          return;
        }
        const [next, ...rest] = delays;
        if (next === undefined) return;
        timer = setTimeout(() => attempt(rest), next);
      } catch (error) {
        console.warn('Failed to update org group:', error);
      }
    };

    attempt([500, 1000, 2000]);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [isSignedIn, organization?.id]);

  // Effect 3: Consent management (separate concern)
  useEffect(() => {
    if (consentManager === undefined) return;

    if (hasMeasurementConsent && !isVaultTelemetryPath(pathname)) {
      // User has given consent - enable tracking
      posthog.opt_in_capturing();

      // Enable session recording when user has consented
      try {
        if (posthog.sessionRecording) {
          posthog.startSessionRecording();
        }
      } catch {
        // ignore
      }
    } else {
      // User has not given consent or declined - disable tracking
      posthog.opt_out_capturing();

      // Ensure session recording is stopped
      try {
        if (posthog.sessionRecording) {
          posthog.stopSessionRecording();
        }
      } catch {
        // ignore
      }
    }
  }, [hasMeasurementConsent, consentManager, pathname]);

  useEffect(() => {
    const stopOnSensitiveSurface = () => {
      if (vaultTelemetryBlocked() && !posthog.config.disable_session_recording) {
        posthog.stopSessionRecording();
      }
    };
    stopOnSensitiveSurface();
    const observer = new MutationObserver(stopOnSensitiveSurface);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  return null;
}

// Wrap PostHogPageView in Suspense to avoid the useSearchParams usage above
// from de-opting the whole app into client-side rendering
// See: https://nextjs.org/docs/messages/deopted-into-client-rendering
function SuspendedPostHogPageView() {
  return (
    <Suspense fallback={null}>
      <PostHogPageView />
    </Suspense>
  );
}
